/* SINCE YOU LEFT and the DIGEST (docs/design/inbox.md §7, R1851; comps D
   and E of .impeccable/mocks/inbox/, round 089 cards 19 a to 21 a). The
   markup is `ui/inbox/sinceleft.ts` and `ui/inbox/digest.ts`.

   Every rule reads the system tokens of gen/tokens.css and the inks by
   role (ui-map-interface.md §2.3 and §2.4): a label in the cap style and
   the dim ink, a value in the text ink, a press in the accent on the dark
   face (087 card 03), a lit range as a plate with an accent bar and never
   a solid light press (R1753). Only the danger ink and the good ink mark
   a figure; WATER and WORKS keep a neutral mark, and gold marks nothing
   (comp D). Every rule stands under #ui, because `#ui button` sets the
   face of every chrome button and an id outranks a class. */

@import '../../gen/tokens.css';

/* --- the folded line (comp E, folded) --- */
#ui .sl { margin: 0 0 var(--s-3); }
#ui .sl .pos { color: var(--good); }
#ui .sl .neg { color: var(--bad); }
#ui .sl .dg-z,
#ui .dg .dg-z { color: var(--faint); }
#ui .sl-line {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  width: 100%;
  min-height: var(--target-mouse);
  margin: 0;
  padding: 0 0 0 var(--s-1);
  border: 0;
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.1);
  border-radius: 0;
  background: transparent;
  color: var(--text);
  font: var(--t-small);
  text-align: left;
  white-space: nowrap;
}
#ui .sl-line .sl-name {
  flex: none;
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
  color: var(--dim);
}
#ui .sl-line .sl-away { flex: none; color: var(--hero-ink); font-variant-numeric: tabular-nums; }
#ui .sl-line .sl-sum {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}
#ui .sl-line .sl-dot { color: var(--faint); }
/* The ▾ of the folded line and the ▴ of the open block wear the square
   press of the chrome (R1856, R1857, `ui/arrowpress.ts`): the 24 px face
   with the 12 px arrow, as the chat, the queue line and the context sheet
   (R1876). The chevron of the line is a span inside the line's press; the
   fold is a press of its own. */
#ui .sl-chev { flex: none; display: inline-grid; }

/* --- the open block (comp E, open) --- */
#ui .sl-open {
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
  padding: var(--s-2);
  border: 1px solid rgb(var(--accent-rgb) / 0.2);
  border-radius: var(--r-card);
  background: rgb(var(--deep-rgb) / 0.35);
}
/* The range row: four segments and the ▴ square. */
#ui .sl-rr { display: flex; align-items: center; gap: var(--s-2); }
/* The range strip is the tab strip of the choice kit (R2011); it takes
   the room the ▴ square leaves. */
#ui .sl-rr > .ch-ts { flex: 1 1 auto; }
#ui .sl-rr > .ch-ts > button.ch-tab { padding: 0 var(--s-2); }
/* The range line: dim, one line. */
#ui .sl-span {
  padding: 0 var(--s-1);
  font: var(--t-small);
  color: var(--dim);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-variant-numeric: tabular-nums;
}
/* The rows: one line each, a mark, the label, the detail, the figure. */
#ui .sl-rows {
  border: 1px solid rgb(var(--accent-rgb) / 0.14);
  border-radius: var(--r-button);
}
#ui .sl-row {
  display: flex;
  align-items: center;
  gap: var(--s-3);
  min-height: 30px;
  padding: 0 var(--s-3);
  font: var(--t-small);
}
#ui .sl-row + .sl-row { border-top: 1px solid rgb(var(--accent-rgb) / 0.08); }
#ui .sl-row .sl-mk { flex: none; width: 8px; height: 8px; border-radius: 1px; background: var(--dim); }
#ui .sl-row.bad .sl-mk { background: var(--bad); }
#ui .sl-row .sl-lab {
  flex: none;
  width: 11ch;
  font: var(--t-cap);
  letter-spacing: 0.06em;
  color: var(--dim);
  overflow: hidden;
  text-overflow: ellipsis;
}
#ui .sl-row[data-k="siege"].bad .sl-lab { color: var(--bad); }
#ui .sl-row .sl-det {
  flex: 1;
  min-width: 0;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
#ui .sl-row .sl-fig {
  flex: none;
  color: var(--text);
  font: var(--t-body);
  font-variant-numeric: tabular-nums;
  text-align: right;
  white-space: nowrap;
}
#ui .sl-row.dim .sl-mk { background: var(--faint); }
/* An ended siege (R3273): the danger ink for a window the side lost, the
   good ink for one it held. A row with a message is a press, and it keeps
   the face of the row. */
#ui .sl-row.bad .sl-fig { color: var(--bad); }
#ui .sl-row.good .sl-mk { background: var(--good); }
#ui .sl-row.good .sl-fig { color: var(--good); }
#ui button.sl-row {
  width: 100%;
  margin: 0;
  border: 0;
  border-radius: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
#ui button.sl-row + .sl-row, #ui .sl-row + button.sl-row { border-top: 1px solid rgb(var(--accent-rgb) / 0.08); }
#ui .sl-row.dim .sl-lab,
#ui .sl-row.dim .sl-det,
#ui .sl-row.dim .sl-fig { color: var(--faint); }
#ui .sl-quiet { padding: var(--s-3); font: var(--t-small); color: var(--dim); }
/* The three figure cells: a spark, the label, the figure and its change. */
#ui .sl-figs { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: var(--s-2); }
#ui .sl-fc {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  min-width: 0;
  padding: var(--s-2) var(--s-3);
  border: 1px solid rgb(var(--accent-rgb) / 0.14);
  border-radius: var(--r-button);
}
#ui .sl-spark { flex: 1 1 auto; min-width: 20px; max-width: 54px; height: 15px; display: block; }
#ui .sl-spark polyline {
  fill: none;
  stroke: var(--text);
  stroke-width: 1.2;
  stroke-linejoin: round;
  vector-effect: non-scaling-stroke;
}
#ui .sl-spark circle { fill: var(--text); }
#ui .sl-spark.sp-iso polyline { stroke: var(--isotopes); }
#ui .sl-spark.sp-iso circle { fill: var(--isotopes); }
#ui .sl-spark.flat polyline { stroke: var(--faint); }
#ui .sl-spark.flat circle { fill: var(--faint); }
#ui .sl-ft { flex: none; display: flex; flex-direction: column; min-width: 0; }
#ui .sl-ft i {
  font: var(--t-cap);
  font-style: normal;
  letter-spacing: 0.06em;
  color: var(--dim);
  white-space: nowrap;
}
#ui .sl-ft b { font: var(--t-body); font-weight: 500; color: var(--text); white-space: nowrap; font-variant-numeric: tabular-nums; }
#ui .sl-ft em { font: var(--t-cap); font-style: normal; color: var(--dim); white-space: nowrap; }
/* ALL FIGURES ›: a quiet text row with a hairline top edge. */
#ui .sl-all {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: var(--target-mouse);
  margin: 0;
  padding: 0 var(--s-3);
  border: 0;
  border-top: 1px solid rgb(var(--accent-rgb) / 0.14);
  border-radius: 0;
  background: transparent;
  color: var(--dim);
  font: var(--t-cap);
  letter-spacing: var(--t-cap-track);
}
#ui .sl-all i { font: var(--t-title); font-style: normal; color: var(--accent); }
/* SHOW ONLY THESE and MARK THESE READ: two equal presses on the dark face. */
#ui .sl-acts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--s-2); }
#ui .sl-b,
#ui .dg-back,
#ui .dg-mo {
  min-height: var(--target-mouse);
  margin: 0;
  padding: 0 var(--s-2);
  border: 1px solid rgb(var(--accent-rgb) / 0.34);
  border-radius: var(--r-button);
  background: rgb(var(--deep-rgb) / 0.88);
  color: var(--accent);
  font: var(--t-cap);
  letter-spacing: 0.1em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
#ui .sl-b.on {
  background: rgb(var(--accent-rgb) / 0.18);
  border-color: rgb(var(--accent-rgb) / 0.6);
  box-shadow: inset 0 -2px 0 var(--accent);
}
#ui .sl-b:disabled { opacity: 1; color: var(--faint); border-color: rgb(var(--text-rgb) / 0.14); }

/* --- the DIGEST page (round 089 cards 20 a and 21 a) --- */
#ui .dg { display: flex; flex-direction: column; gap: var(--s-3); }
#ui .dg .pos { color: var(--good); }
#ui .dg .neg { color: var(--bad); }
#ui .dg-bar { display: flex; align-items: center; gap: var(--s-3); }
#ui .dg-back { flex: none; }
#ui .dg-h { font: var(--t-title); letter-spacing: var(--t-title-track); color: var(--hero-ink); }
/* The range row and the range line stay pinned, so each screen of the
   page says its range (card 21). The order is local to the tab body: the
   pin stands over the pinned columns of the ledger table. */
#ui .dg-pin {
  position: sticky;
  top: 0;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: var(--s-2);
  padding: var(--s-2) 0;
  background: rgb(var(--sheet-rgb));
  border-bottom: 1px solid rgb(var(--accent-rgb) / 0.14);
}
#ui .dg-cb { display: flex; flex-direction: column; gap: var(--s-2); }
#ui .dg-sh { display: flex; align-items: baseline; gap: var(--s-3); margin: 0; flex-wrap: wrap; }
#ui .dg-sh b { font: var(--t-cap); letter-spacing: var(--t-cap-track); color: var(--text); }
#ui .dg-sh i { font: var(--t-small); font-style: normal; color: var(--dim); }
/* A chart over its figure grid: the label column, the slots, the total. */
#ui .dg-g {
  display: grid;
  grid-template-columns: 52px minmax(0, 1fr) 44px;
  column-gap: var(--s-2);
  row-gap: 2px;
  align-items: center;
}
#ui .dg-gc svg { display: block; width: 100%; height: auto; }
#ui .dg-gv { display: grid; grid-template-columns: repeat(var(--n), minmax(0, 1fr)); }
#ui .dg-gv i {
  font: var(--t-cap);
  font-style: normal;
  letter-spacing: 0;
  text-align: center;
  color: var(--text);
  white-space: nowrap;
  overflow: hidden;
  font-variant-numeric: tabular-nums;
}
#ui .dg-gl {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  font: var(--t-cap);
  letter-spacing: 0.04em;
  color: var(--dim);
  white-space: nowrap;
  overflow: hidden;
}
#ui .dg-gt {
  font: var(--t-small);
  color: var(--hero-ink);
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
#ui .dg-gx,
#ui .dg-gx i { color: var(--faint); }
#ui .dg-k { flex: none; width: 7px; height: 7px; border-radius: 1px; background: var(--dim); }
#ui .dg-k.dg-up,
#ui .dg-k.dg-held { background: var(--text); }
#ui .dg-k.dg-dn { background: var(--bad); }
#ui .dg-k.dg-good { background: var(--good); }
#ui .dg-k.dg-iso { background: var(--isotopes); }
#ui .dg-k.dg-core { background: var(--core); }
#ui .dg-k.dg-faint { background: var(--faint); }
/* The inks of the charts. */
#ui .dg-ch .dg-up,
#ui .dg-ch .dg-held { fill: var(--text); }
#ui .dg-ch .dg-dn { fill: var(--bad); }
#ui .dg-ch .dg-good { fill: var(--good); }
#ui .dg-ch .dg-iso { fill: var(--isotopes); }
#ui .dg-ch .dg-core { fill: var(--core); }
#ui .dg-ch .dg-x { fill: none; stroke: var(--bad); stroke-width: 1.6; stroke-linecap: round; }
#ui .dg-ch .dg-base { fill: none; stroke: var(--faint); stroke-opacity: 0.6; }
#ui .dg-ch .dg-lane { fill: none; stroke: rgb(var(--text-rgb) / 0.08); }
/* The top moments: a head line over the row of the message, one press. */
#ui .dg-mo {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--s-1);
  width: 100%;
  padding: var(--s-2) var(--s-3);
  text-align: left;
  white-space: normal;
}
#ui .dg-mh { display: flex; justify-content: space-between; gap: var(--s-3); }
#ui .dg-mh b { color: var(--dim); font-weight: 500; }
#ui .dg-mh i { font-style: normal; color: var(--accent); }
#ui .dg-mr {
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: var(--text);
  font: var(--t-small);
  letter-spacing: 0;
}
#ui .dg-mr > * { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
/* The ledger table: the figures as rows, the ranges as columns. The
   gutter and the label stay pinned, and the range columns scroll
   sideways inside the table on a telephone (card 21). */
#ui .dg-lt { overflow-x: auto; overscroll-behavior-x: contain; scrollbar-width: thin; }
#ui .dg-lt table { border-collapse: separate; border-spacing: 0; font: var(--t-small); }
#ui .dg-lt th {
  height: 30px;
  padding: 0 var(--s-2) var(--s-1);
  vertical-align: bottom;
  font: var(--t-cap);
  letter-spacing: 0.04em;
  color: var(--dim);
  text-align: left;
}
#ui .dg-lt td { height: 22px; padding: 0 var(--s-2); white-space: nowrap; }
#ui .dg-lt .r {
  min-width: 52px;
  text-align: right;
  color: var(--text);
  font-variant-numeric: tabular-nums;
}
#ui .dg-lt th.r { color: var(--dim); }
#ui .dg-lt .on { background: rgb(var(--accent-rgb) / 0.07); color: var(--hero-ink); }
#ui .dg-lt th.on { color: var(--accent); }
#ui .dg-lt .dg-tg,
#ui .dg-lt .dg-tl { position: sticky; background: rgb(var(--sheet-rgb)); }
#ui .dg-lt .dg-tg { left: 0; width: 16px; min-width: 16px; padding: 0 0 0 var(--s-1); color: var(--dim); }
#ui .dg-lt .dg-tl {
  left: 18px;
  width: 104px;
  min-width: 104px;
  max-width: 104px;
  overflow: hidden;
  text-overflow: ellipsis;
  font: var(--t-cap);
  letter-spacing: 0.04em;
  color: var(--dim);
  box-shadow: 4px 0 6px -4px rgb(var(--deep-rgb));
}
#ui .dg-lt tr.dg-g1 td { border-top: 1px solid rgb(var(--accent-rgb) / 0.28); }
#ui .dg-lt .dg-good { color: var(--good); }
#ui .dg-lt .dg-bad { color: var(--bad); }
#ui .dg-lt .dg-dim { color: var(--dim); }

@media (hover: hover) {
  #ui .sl-all:hover { background: rgb(var(--accent-rgb) / 0.08); color: var(--hover-ink); }
  #ui .sl-b:hover:not(:disabled),
  #ui .dg-back:hover,
  #ui .dg-mo:hover { background: rgb(var(--accent-rgb) / 0.14); }
}

/* A finger takes a press of 44 px (ui-map-interface.md §12). The rows of
   the block are no press and keep their height. */
@media (pointer: coarse) {
  #ui .sl-line,
  #ui .sl-all,
  #ui .sl-b,
  #ui .dg-back,
  #ui .dg-mo { min-height: var(--target-finger); }
}
